import { useProductInfo } from "@expert/contentful";
import { getLogger } from "@expert/logging";
import { Divider, Flex, Stack, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { usePartnerFeatureConfigured } from "@/partner/usePartnerConfig";
import { useProductOfferStore } from "@/sales-tools";
import { HomeProductFlow } from "@/sales-tools/home-product/components/HomeProductFlow";
import { EligibilityStatus } from "@/sales-tools/home-product/types";
import { ShieldXIcon } from "@/sales-tools/icons/ShieldXIcon";
import { PlanCard } from "@/sales-tools/unified-sales-view/components/PlanCard";
import { SaleTracker } from "@/sales-tools/unified-sales-view/components/SaleTracker";
import { getSelectedSessionId, usePartner, useSessionId } from "@/sdk";
import { userCache } from "@/shared-utils";

const logger = getLogger({
    module: "unified-sales-view",
    supportTeam: "sales",
});

export function UnifiedSalesView({ isChat = false }: { isChat?: boolean }) {
    const partner = usePartner();
    const sessionId = useSessionId();
    const { getProducts, updateSelectedProduct, getSelectedProduct } = useProductOfferStore();
    const products = getProducts(sessionId);
    const selectedProduct = getSelectedProduct(sessionId);
    const [opened, { open, close }] = useDisclosure(false);
    const { isSalesQuestionnaireEnabled = false } = usePartnerFeatureConfigured();

    const { productInfo, isLoading, error } = useProductInfo(
        partner,
        userCache.employeeId,
        products.map((product) => product.productSku),
    );

    if (isLoading) return null;
    if (error) {
        logger.error({ partner, sessionId: getSelectedSessionId(), error }, "Error while trying to fetch product info");
    }
    const eligibleProducts = products.filter(
        (p) => productInfo && productInfo[p.productSku] && p.status === EligibilityStatus.Eligible,
    );

    return (
        <>
            <Flex h="100%" style={{ overflow: "auto" }} direction="column" mt="sm">
                <Text ml="xxs" size="sm" fw="var(--mantine-font-weight-heavy)" c="dark.0">
                    {isChat ? "Available plans" : "Potential upgrades"}
                </Text>
                <Divider mt="md" mb="md" color="primary.5" />

                {selectedProduct?.isPrimaryProduct ? (
                    <HomeProductFlow onPrimarySaleComplete={() => updateSelectedProduct(sessionId, undefined)} />
                ) : !error && productInfo && products.length > 0 ? (
                    <Stack gap="0.75rem" p="sm">
                        {eligibleProducts.length > 0 ? (
                            eligibleProducts.map((p, index) => (
                                <PlanCard
                                    saleProgress={p.saleProgress}
                                    onClick={() => {
                                        updateSelectedProduct(sessionId, p);
                                        if (!p.isPrimaryProduct) {
                                            open();
                                        }
                                    }}
                                    isRecommended={index === 0 && isSalesQuestionnaireEnabled}
                                    key={p.productSku}
                                    product={productInfo[p.productSku]}
                                    partner={partner}
                                    variant={isChat ? "full" : "compact"}
                                    showEligibleTag={isChat}
                                />
                            ))
                        ) : (
                            <Flex mt="md">
                                <ShieldXIcon />
                                <Text ml="3xs" size="xs" c="dark.2">
                                    Not eligible for additional plans
                                </Text>
                            </Flex>
                        )}
                    </Stack>
                ) : (
                    <Flex mt="md" p="sm">
                        <ShieldXIcon />
                        <Text ml="3xs" size="xs" c="dark.2">
                            Not eligible for any upgrades
                        </Text>
                    </Flex>
                )}
            </Flex>
            {selectedProduct && (
                <SaleTracker
                    productInfo={productInfo?.[selectedProduct.productSku]}
                    selectedProduct={selectedProduct}
                    opened={opened}
                    onClose={close}
                />
            )}
        </>
    );
}
